/**
 * Карта v3 — движок: ввод, состояние, цикл отрисовки.
 *
 * React владеет только панелями вокруг. Холст живёт здесь и полностью
 * императивен: ни одно движение мыши не вызывает перерисовку React-дерева.
 * Наружу движок отдаёт события (выделение, изменение, статус) — на них
 * страница обновляет свои панели.
 */
import { GRID, ZOOM_MIN, ZOOM_MAX, ZOOM_STEP, HIT } from './const';
import {
  screenToWorld, zoomAt, fitTo, snap, containsPt, nearestOnPolyline, rectsOverlap,
} from './math';
import { render, renderOverlay } from './render';
import { computePorts, nearestPortSlot } from './ports';
import { routeAll, edgeAnchor, resolveVia } from './router';
import { sceneBounds, assignPortSeats, serialize, serializeMeta } from './scene';
import { autoLayout, layoutInside, separateOverlaps, keepInside, moveContainer } from './layout';
import { setSpriteListener } from './sprites';

const DRAG_THRESHOLD = 3;   // px: меньше — это дрожание руки, а не жест

export function createEngine(canvas, overlay, scene, opts = {}) {
  const listeners = new Map();
  const emit = (ev, data) => (listeners.get(ev) || []).forEach((f) => f(data));

  const ui = {
    theme: opts.theme || 'light',
    grid: opts.grid !== false,
    labels: opts.labels || 'type',
    showPorts: opts.showPorts !== false,
    images: opts.images !== false,
    hops: true,
    selection: new Set(),
    hover: null,
    marquee: null,
    guides: null,
    filterActive: false,
    filtered: new Set(),
    viaPoints: new Map(),
  };

  let vp = { x: 0, y: 0, scale: 1 };
  let view = { w: 100, h: 100 };
  let dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
  let needsDraw = true;
  let disposed = false;

  const ctx = canvas.getContext('2d');
  const octx = overlay.getContext('2d');

  const history = [];
  let histIndex = -1;
  let pendingSave = null;
  let dirtySinceSave = false;

  // ------------------------------------------------------------- геометрия

  function reroute() {
    assignPortSeats(scene);
    computePorts(scene, edgeAnchor(scene));
    const stat = routeAll(scene, { nudge: true });
    // позиции ручек изгибов в мировых координатах — для оверлея и попадания
    ui.viaPoints.clear();
    for (const e of scene.edges.values()) {
      const pts = e.route ? e.route.pts : null;
      (e.vias || []).forEach((v, i) => {
        const p = resolveVia(v, scene);
        let at = p;
        if (pts && pts.length >= 4) {
          const near = nearestOnPolyline(pts, p.x, p.y);
          // если линия прошла мимо точки, сажаем ручку на линию — тянуть за
          // ручку, висящую в стороне от провода, невозможно
          if (near.d > 2) at = { x: near.x, y: near.y };
        }
        ui.viaPoints.set(`${e.id}:${i}`, at);
      });
    }
    emit('status', { routed: stat.routed, approx: stat.approx || 0, ms: Math.round(stat.ms) });
    needsDraw = true;
  }

  function resize() {
    const r = canvas.getBoundingClientRect();
    view = { w: Math.max(1, r.width), h: Math.max(1, r.height) };
    dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    for (const cv of [canvas, overlay]) {
      cv.width = Math.round(view.w * dpr);
      cv.height = Math.round(view.h * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    octx.setTransform(dpr, 0, 0, dpr, 0, 0);
    needsDraw = true;
  }

  function frame() {
    if (disposed) return;
    if (needsDraw) {
      render(ctx, scene, vp, view, ui);
      renderOverlay(octx, scene, vp, view, ui);
      needsDraw = false;
    }
    requestAnimationFrame(frame);
  }

  // ---------------------------------------------------------- попадание

  /** Что под курсором. Порядок важен: мелкие цели выигрывают у крупных. */
  function pick(sx, sy) {
    const w = screenToWorld(vp, sx, sy);
    const tol = (px) => px / vp.scale;

    // 1. ручки изгибов у выделенных связей
    for (const id of ui.selection) {
      const e = scene.edges.get(id);
      if (!e) continue;
      const vias = e.vias || [];
      for (let i = vias.length - 1; i >= 0; i--) {
        const p = ui.viaPoints.get(`${e.id}:${i}`);
        if (p && Math.hypot(p.x - w.x, p.y - w.y) <= tol(HIT.HANDLE)) {
          return { type: 'via', id: e.id, index: i };
        }
      }
      // 2. порты выделенной связи
      for (const end of ['a', 'b']) {
        const pt = e[`${end}Pt`];
        if (pt && Math.hypot(pt.x - w.x, pt.y - w.y) <= tol(HIT.PORT)) {
          return { type: 'port', id: e.id, end };
        }
      }
      // 3. сама линия: тянешь в любом месте — там и появляется изгиб
      if (e.route) {
        const r = nearestOnPolyline(e.route.pts, w.x, w.y);
        if (r.d <= tol(HIT.EDGE + 2)) {
          return { type: 'segment', id: e.id, seg: r.seg, at: { x: r.x, y: r.y } };
        }
      }
    }

    // 4. устройства
    const nodes = [...scene.nodes.values()];
    for (let i = nodes.length - 1; i >= 0; i--) {
      const n = nodes[i];
      if (n.hidden) continue;
      if (containsPt(n, w.x, w.y)) return { type: 'node', id: n.id };
    }

    // 5. трассы
    let best = null, bestD = Infinity;
    for (const e of scene.edges.values()) {
      if (!e.route) continue;
      const r = nearestOnPolyline(e.route.pts, w.x, w.y);
      if (r.d < bestD && r.d <= tol(HIT.EDGE)) { bestD = r.d; best = { type: 'edge', id: e.id, seg: r.seg }; }
    }
    if (best) return best;

    // 6. рамка/уголок локации — от самой глубокой к верхней
    const conts = [...scene.conts.values()].sort((a, b) => b.depth - a.depth);
    for (const c of conts) {
      const t = tol(HIT.RESIZE + 4);
      const nearL = Math.abs(w.x - c.x) <= t, nearR = Math.abs(w.x - (c.x + c.w)) <= t;
      const nearT = Math.abs(w.y - c.y) <= t, nearB = Math.abs(w.y - (c.y + c.h)) <= t;
      const inBand = w.x >= c.x - t && w.x <= c.x + c.w + t
                  && w.y >= c.y - t && w.y <= c.y + c.h + t;
      if (inBand && (nearL || nearR) && (nearT || nearB)) {
        const corner = `${nearT ? 'n' : 's'}${nearL ? 'w' : 'e'}`;
        return { type: 'cont-resize', id: c.id, corner };
      }
      // заголовок — за него локация таскается целиком
      if (containsPt({ x: c.x, y: c.y, w: c.w, h: 38 }, w.x, w.y)) return { type: 'cont', id: c.id };
      const edgeBand = containsPt(c, w.x, w.y) &&
        (Math.abs(w.x - c.x) <= t || Math.abs(w.x - (c.x + c.w)) <= t ||
         Math.abs(w.y - c.y) <= t || Math.abs(w.y - (c.y + c.h)) <= t);
      if (edgeBand) return { type: 'cont', id: c.id };
    }
    return null;
  }

  // ------------------------------------------------------------- история

  function snapshot() {
    return JSON.stringify({
      nodes: [...scene.nodes.values()].map((n) => [n.id, n.x, n.y, n.hidden ? 1 : 0, n.pinned ? 1 : 0]),
      conts: [...scene.conts.values()].map((c) => [c.id, c.x, c.y, c.w, c.h, c.pinned ? 1 : 0]),
      edges: [...scene.edges.values()].map((e) => [e.id, e.mode, JSON.stringify(e.vias || []),
        JSON.stringify(e.aPort), JSON.stringify(e.bPort)]),
    });
  }

  function restore(json) {
    const d = JSON.parse(json);
    for (const [id, x, y, hidden, pinned] of d.nodes) {
      const n = scene.nodes.get(id);
      if (n) { n.x = x; n.y = y; n.hidden = !!hidden; n.pinned = !!pinned; }
    }
    for (const [id, x, y, w, h, pinned] of d.conts) {
      const c = scene.conts.get(id);
      if (c) { c.x = x; c.y = y; c.w = w; c.h = h; c.pinned = !!pinned; }
    }
    for (const [id, mode, vias, aPort, bPort] of d.edges) {
      const e = scene.edges.get(id);
      if (e) { e.mode = mode; e.vias = JSON.parse(vias); e.aPort = JSON.parse(aPort); e.bPort = JSON.parse(bPort); }
    }
    reroute();
  }

  function commit(label) {
    const snap0 = snapshot();
    history.splice(histIndex + 1);
    history.push(snap0);
    if (history.length > 80) history.shift();
    histIndex = history.length - 1;
    emit('history', { canUndo: histIndex > 0, canRedo: false, label });
    scheduleSave();
  }

  function undo() {
    if (histIndex <= 0) return;
    histIndex--;
    restore(history[histIndex]);
    emit('history', { canUndo: histIndex > 0, canRedo: histIndex < history.length - 1 });
    scheduleSave();
  }

  function redo() {
    if (histIndex >= history.length - 1) return;
    histIndex++;
    restore(history[histIndex]);
    emit('history', { canUndo: histIndex > 0, canRedo: histIndex < history.length - 1 });
    scheduleSave();
  }

  /**
   * Сохранение откладывается на 900 мс, чтобы не бомбить сервер во время
   * перетаскивания. Но отложенное — значит потенциально потерянное: уход со
   * страницы, F5 или закрытие вкладки убивали бы последнюю правку молча.
   * Поэтому есть flushSave(), и он вызывается при уходе и при закрытии.
   */
  function doSave() {
    if (!opts.onSave) return;
    clearTimeout(pendingSave);
    pendingSave = null;
    dirtySinceSave = false;
    const { items, remove } = serialize(scene);
    opts.onSave(items, serializeMeta(scene, vp, ui), remove);
  }

  function scheduleSave() {
    if (!opts.onSave) return;
    dirtySinceSave = true;
    clearTimeout(pendingSave);
    pendingSave = setTimeout(doSave, 900);
  }

  function flushSave() {
    if (dirtySinceSave || pendingSave) doSave();
  }

  function onBeforeUnload(ev) {
    if (!dirtySinceSave && !pendingSave) return;
    flushSave();
    // не блокируем уход диалогом — сохранение уже ушло
    delete ev.returnValue;
  }

  function onVisibility() {
    if (document.visibilityState === 'hidden') flushSave();
  }

  // ----------------------------------------------------------- жесты

  let drag = null;

  function onPointerDown(ev) {
    if (ev.button === 1 || (ev.button === 0 && ev.altKey)) {
      drag = { mode: 'pan', sx: ev.clientX, sy: ev.clientY, vp0: { ...vp } };
      canvas.setPointerCapture(ev.pointerId);
      return;
    }
    if (ev.button !== 0) return;

    const rect = canvas.getBoundingClientRect();
    const sx = ev.clientX - rect.left, sy = ev.clientY - rect.top;
    const hit = pick(sx, sy);
    const w = screenToWorld(vp, sx, sy);
    canvas.setPointerCapture(ev.pointerId);

    if (!hit) {
      if (!ev.shiftKey && !ev.ctrlKey) { ui.selection.clear(); emitSel(); }
      drag = { mode: 'marquee', sx, sy, add: ev.shiftKey || ev.ctrlKey };
      needsDraw = true;
      return;
    }

    // --- изгиб линии: тянем существующую ручку ---
    if (hit.type === 'via') {
      const e = scene.edges.get(hit.id);
      drag = { mode: 'via', edge: e, index: hit.index, moved: false, sx0: sx, sy0: sy };
      return;
    }

    // --- изгиб линии: тянем середину сегмента и создаём новый изгиб ---
    if (hit.type === 'segment') {
      const e = scene.edges.get(hit.id);
      const at = hit.at || { x: w.x, y: w.y };
      const index = insertViaSorted(e, at.x, at.y);
      e.mode = e.mode === 'auto' ? 'guided' : e.mode;
      drag = { mode: 'via', edge: e, index, moved: false, created: true, sx0: sx, sy0: sy };
      reroute();
      return;
    }

    // --- перетаскивание порта по периметру устройства ---
    if (hit.type === 'port') {
      const e = scene.edges.get(hit.id);
      drag = { mode: 'port', edge: e, end: hit.end, moved: false, sx0: sx, sy0: sy };
      return;
    }

    if (hit.type === 'cont-resize') {
      const c = scene.conts.get(hit.id);
      drag = {
        mode: 'resize', cont: c, corner: hit.corner,
        x0: c.x, y0: c.y, w0: c.w, h0: c.h,
        wx: w.x, wy: w.y, moved: false, sx0: sx, sy0: sy,
      };
      select(hit.id, ev);
      return;
    }

    select(hit.id, ev);

    const ents = [...ui.selection].map((id) => scene.nodes.get(id) || scene.conts.get(id)).filter(Boolean);
    if (hit.type === 'node' || hit.type === 'cont') {
      drag = {
        mode: 'move',
        wx: w.x, wy: w.y,
        items: ents.map((e2) => ({ ent: e2, x0: e2.x, y0: e2.y })),
        primary: hit.id,
        moved: false, sx0: sx, sy0: sy,
      };
    }
  }

  /**
   * Вставка изгиба с пересчётом порядка ВСЕХ точек.
   *
   * Порядок — это положение точки на маршруте (номер сегмента + доля внутри).
   * После каждого изгиба маршрут перестраивается и сегментов становится больше,
   * поэтому старые значения оказываются в другой системе отсчёта. Если их не
   * пересчитать, второй изгиб на той же линии встаёт не на своё место и трасса
   * делает петлю: порт → дальняя точка → назад к ближней → порт.
   */
  function insertViaSorted(e, x, y) {
    const pts = e.route ? e.route.pts : [];
    const posOf = (px, py) => {
      if (pts.length < 4) return 0;
      const r = nearestOnPolyline(pts, px, py);
      return r.seg + r.t;
    };
    const list = (e.vias || []).slice();
    for (const v of list) {
      const p = resolveVia(v, scene);
      v.order = posOf(p.x, p.y);
    }
    const nv = { kind: 'point', x, y, order: posOf(x, y), anchor: { mode: 'world' } };
    list.push(nv);
    list.sort((p, q) => (p.order || 0) - (q.order || 0));
    e.vias = list;
    return list.indexOf(nv);
  }

  function onPointerMove(ev) {
    const rect = canvas.getBoundingClientRect();
    const sx = ev.clientX - rect.left, sy = ev.clientY - rect.top;

    if (!drag) {
      const hit = pick(sx, sy);
      const changed = JSON.stringify(hit) !== JSON.stringify(ui.hover);
      ui.hover = hit;
      canvas.style.cursor =
        !hit ? 'default'
        : hit.type === 'via' || hit.type === 'segment' ? 'grab'
        : hit.type === 'port' ? 'crosshair'
        : hit.type === 'cont-resize'
          ? (hit.corner === 'nw' || hit.corner === 'se' ? 'nwse-resize' : 'nesw-resize')
        : hit.type === 'edge' ? 'pointer'
        : 'move';
      if (changed) needsDraw = true;
      return;
    }

    if (drag.sx0 !== undefined && !drag.moved
        && Math.hypot(sx - drag.sx0, sy - drag.sy0) <= DRAG_THRESHOLD) return;

    const w = screenToWorld(vp, sx, sy);

    if (drag.mode === 'pan') {
      vp = { ...vp, x: drag.vp0.x + (ev.clientX - drag.sx), y: drag.vp0.y + (ev.clientY - drag.sy) };
      needsDraw = true;
      return;
    }

    if (drag.mode === 'marquee') {
      ui.marquee = {
        x: Math.min(drag.sx, sx), y: Math.min(drag.sy, sy),
        w: Math.abs(sx - drag.sx), h: Math.abs(sy - drag.sy),
      };
      needsDraw = true;
      return;
    }

    if (drag.mode === 'via') {
      const v = drag.edge.vias[drag.index];
      if (!v) return;
      v.x = ev.shiftKey ? w.x : snap(w.x, GRID);
      v.y = ev.shiftKey ? w.y : snap(w.y, GRID);
      v.anchor = { mode: 'world' };
      drag.moved = true;
      drag.reorder = true;
      reroute();
      return;
    }

    if (drag.mode === 'port') {
      const e = drag.edge;
      const node = scene.nodes.get(drag.end === 'a' ? e.a : e.b);
      if (!node) return;
      const slot = nearestPortSlot(node, w.x, w.y);
      e[drag.end === 'a' ? 'aPort' : 'bPort'] = { mode: 'pinned', side: slot.side, t: slot.t };
      drag.moved = true;
      reroute();
      return;
    }

    if (drag.mode === 'resize') {
      const c = drag.cont;
      const dx = w.x - drag.wx, dy = w.y - drag.wy;
      const co = drag.corner || 'se';
      const MINW = 200, MINH = 140;

      // Тянем за угол: восточные меняют ширину, западные — ещё и левый край.
      // Содержимое при этом НЕ трогаем: рамку растягивают, чтобы дать место,
      // а не чтобы растащить устройства.
      if (co.includes('e')) c.w = Math.max(MINW, drag.w0 + dx);
      if (co.includes('s')) c.h = Math.max(MINH, drag.h0 + dy);
      if (co.includes('w')) {
        const nw = Math.max(MINW, drag.w0 - dx);
        c.x = drag.x0 + (drag.w0 - nw);
        c.w = nw;
      }
      if (co.includes('n')) {
        const nh = Math.max(MINH, drag.h0 - dy);
        c.y = drag.y0 + (drag.h0 - nh);
        c.h = nh;
      }
      c.autoSize = false;      // размер задан руками — больше не пересчитываем
      c.pinned = true;
      drag.moved = true;
      reroute();
      return;
    }

    if (drag.mode === 'move') {
      let dx = w.x - drag.wx, dy = w.y - drag.wy;
      if (!ev.shiftKey) {
        const first = drag.items[0];
        if (first) {
          dx = snap(first.x0 + dx, GRID) - first.x0;
          dy = snap(first.y0 + dy, GRID) - first.y0;
        }
      }
      for (const it of drag.items) {
        if (it.ent.kind === 'cont') moveContainer(scene, it.ent, it.x0 + dx - it.ent.x, it.y0 + dy - it.ent.y);
        else { it.ent.x = it.x0 + dx; it.ent.y = it.y0 + dy; }
        it.ent.pinned = true;
      }
      ui.guides = computeGuides(drag.items.map((i) => i.ent));
      drag.moved = true;
      // ВАЖНО: во время жеста не зовём keepInside. Он поджимает детей внутрь
      // рамки, и при перетаскивании локации её содержимое сминается в кучу —
      // со стороны это выглядит как «устройства уезжают сами по себе».
      // Содержимое едет вместе с рамкой через moveContainer, а выравнивание
      // и разведение делаем один раз, когда кнопку отпустили.
      reroute();
    }
  }

  function onPointerUp(ev) {
    if (!drag) return;
    const d = drag;
    drag = null;
    ui.guides = null;

    if (d.mode === 'marquee') {
      const m = ui.marquee;
      ui.marquee = null;
      if (m && (m.w > 3 || m.h > 3)) {
        const a = screenToWorld(vp, m.x, m.y);
        const b = screenToWorld(vp, m.x + m.w, m.y + m.h);
        const box = { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) };
        if (!d.add) ui.selection.clear();
        for (const n of scene.nodes.values()) if (!n.hidden && rectsOverlap(box, n)) ui.selection.add(n.id);
        emitSel();
      }
      needsDraw = true;
      return;
    }

    if (d.moved) {
      if (d.mode === 'move' || d.mode === 'resize') {
        // порядок важен: развели соседей → вместили детей → проверили ещё раз
        for (let pass = 0; pass < 3; pass++) {
          const moved = separateOverlaps(scene, d.primary);
          keepInside(scene);
          if (!moved) break;
        }
      }
      reroute();
      commit(d.mode);
    } else if (d.mode === 'via' && d.created) {
      // ручку создали, но не потянули — считаем, что изгиб не нужен
      d.edge.vias.splice(d.index, 1);
      if (!d.edge.vias.length && d.edge.mode === 'guided') d.edge.mode = 'auto';
      reroute();
    }
    needsDraw = true;
  }

  function onDoubleClick(ev) {
    const rect = canvas.getBoundingClientRect();
    const sx = ev.clientX - rect.left, sy = ev.clientY - rect.top;
    const hit = pick(sx, sy);
    if (!hit) return;
    if (hit.type === 'via') {          // двойной клик по ручке — убрать изгиб
      const e = scene.edges.get(hit.id);
      e.vias.splice(hit.index, 1);
      if (!e.vias.length && e.mode === 'guided') e.mode = 'auto';
      reroute(); commit('via-remove');
      return;
    }
    if (hit.type === 'node') emit('open', { type: 'node', id: hit.id });
    if (hit.type === 'cont') { layoutInside(scene, hit.id); reroute(); commit('layout-inside'); }
  }

  function onContextMenu(ev) {
    ev.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const sx = ev.clientX - rect.left, sy = ev.clientY - rect.top;
    const hit = pick(sx, sy);
    if (hit && (hit.type === 'edge' || hit.type === 'segment' || hit.type === 'via')) {
      if (!ui.selection.has(hit.id)) { ui.selection.clear(); ui.selection.add(hit.id); emitSel(); }
    } else if (hit && (hit.type === 'node' || hit.type === 'cont')) {
      if (!ui.selection.has(hit.id)) { ui.selection.clear(); ui.selection.add(hit.id); emitSel(); }
    }
    emit('menu', { hit, x: ev.clientX, y: ev.clientY });
    needsDraw = true;
  }

  function onWheel(ev) {
    ev.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const sx = ev.clientX - rect.left, sy = ev.clientY - rect.top;
    if (ev.ctrlKey || ev.metaKey || Math.abs(ev.deltaY) > Math.abs(ev.deltaX)) {
      const f = ev.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      vp = zoomAt(vp, sx, sy, f, ZOOM_MIN, ZOOM_MAX);
    } else {
      vp = { ...vp, x: vp.x - ev.deltaX, y: vp.y - ev.deltaY };
    }
    needsDraw = true;
    emit('viewport', { ...vp });
  }

  function computeGuides(moving) {
    const guides = [];
    if (moving.length !== 1) return guides;
    const m = moving[0];
    const mcx = m.x + m.w / 2, mcy = m.y + m.h / 2;
    for (const n of scene.nodes.values()) {
      if (n === m || n.hidden) continue;
      const ncx = n.x + n.w / 2, ncy = n.y + n.h / 2;
      if (Math.abs(ncx - mcx) < 2) guides.push({ axis: 'x', v: ncx, from: Math.min(n.y, m.y) - 30, to: Math.max(n.y + n.h, m.y + m.h) + 30 });
      if (Math.abs(ncy - mcy) < 2) guides.push({ axis: 'y', v: ncy, from: Math.min(n.x, m.x) - 30, to: Math.max(n.x + n.w, m.x + m.w) + 30 });
    }
    return guides.slice(0, 6);
  }

  function select(id, ev) {
    if (ev && (ev.shiftKey || ev.ctrlKey)) {
      if (ui.selection.has(id)) ui.selection.delete(id); else ui.selection.add(id);
    } else if (!ui.selection.has(id)) {
      ui.selection.clear();
      ui.selection.add(id);
    }
    emitSel();
    needsDraw = true;
  }

  function emitSel() {
    const ids = [...ui.selection];
    emit('selection', ids.map((id) => {
      if (scene.nodes.has(id)) return { type: 'node', id, ent: scene.nodes.get(id) };
      if (scene.conts.has(id)) return { type: 'cont', id, ent: scene.conts.get(id) };
      if (scene.edges.has(id)) return { type: 'edge', id, ent: scene.edges.get(id) };
      return { type: '?', id };
    }));
  }

  function onKey(ev) {
    const tag = (ev.target && ev.target.tagName) || '';
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    const meta = ev.ctrlKey || ev.metaKey;

    if (meta && ev.key.toLowerCase() === 'z') { ev.preventDefault(); ev.shiftKey ? redo() : undo(); return; }
    if (meta && ev.key.toLowerCase() === 'y') { ev.preventDefault(); redo(); return; }
    if (meta && ev.key.toLowerCase() === 'a') {
      ev.preventDefault();
      ui.selection.clear();
      for (const n of scene.nodes.values()) if (!n.hidden) ui.selection.add(n.id);
      emitSel(); needsDraw = true; return;
    }
    if (ev.key === 'Escape') { ui.selection.clear(); emitSel(); needsDraw = true; return; }
    if (ev.key === 'Delete' || ev.key === 'Backspace') {
      // на карте удаляем только ручные изгибы — данные системы карта не трогает
      let n = 0;
      for (const id of ui.selection) {
        const e = scene.edges.get(id);
        if (e && e.vias.length) { e.vias = []; e.mode = 'auto'; n++; }
      }
      if (n) { reroute(); commit('clear-vias'); emit('toast', `Изгибы сброшены: ${n}`); }
      return;
    }
    if (ev.key === 'f' || ev.key === 'F' || ev.key === 'а' || ev.key === 'А') { fit(); return; }
    if (ev.key === 'g' || ev.key === 'G' || ev.key === 'п' || ev.key === 'П') {
      ui.grid = !ui.grid; needsDraw = true; return;
    }
    const step = ev.shiftKey ? 1 : GRID;
    const move = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[ev.key];
    if (move && ui.selection.size) {
      ev.preventDefault();
      for (const id of ui.selection) {
        const ent = scene.nodes.get(id) || scene.conts.get(id);
        if (!ent) continue;
        if (ent.kind === 'cont') moveContainer(scene, ent, move[0], move[1]);
        else { ent.x += move[0]; ent.y += move[1]; }
        ent.pinned = true;
      }
      keepInside(scene); reroute(); commit('nudge');
    }
  }

  // -------------------------------------------------------------- API

  function fit() {
    vp = fitTo(sceneBounds(scene), view.w, view.h, 70);
    needsDraw = true;
    emit('viewport', { ...vp });
  }

  function zoomBy(f) {
    vp = zoomAt(vp, view.w / 2, view.h / 2, f, ZOOM_MIN, ZOOM_MAX);
    needsDraw = true;
    emit('viewport', { ...vp });
  }

  function focusNode(id) {
    const n = scene.nodes.get(id);
    if (!n) return;
    vp = {
      scale: Math.max(vp.scale, 0.9),
      x: view.w / 2 - (n.x + n.w / 2) * Math.max(vp.scale, 0.9),
      y: view.h / 2 - (n.y + n.h / 2) * Math.max(vp.scale, 0.9),
    };
    ui.selection.clear(); ui.selection.add(id);
    emitSel(); needsDraw = true;
  }

  const api = {
    on(ev, fn) {
      if (!listeners.has(ev)) listeners.set(ev, []);
      listeners.get(ev).push(fn);
      return () => {
        const arr = listeners.get(ev) || [];
        const i = arr.indexOf(fn);
        if (i >= 0) arr.splice(i, 1);
      };
    },
    scene,
    get viewport() { return { ...vp }; },
    get ui() { return ui; },
    resize, fit, zoomBy, focusNode, reroute, undo, redo, flushSave,
    setOption(k, v) {
      ui[k] = v;
      if (['showPorts','labels','theme','grid','hops','images'].includes(k)) needsDraw = true;
      scheduleSave();
    },
    setFilter(ids) {
      ui.filterActive = !!ids;
      ui.filtered = ids || new Set();
      needsDraw = true;
    },
    selectIds(ids) {
      ui.selection = new Set(ids);
      emitSel();
      needsDraw = true;
    },
    autoArrange() {
      autoLayout(scene, { force: true });
      reroute();
      commit('auto-layout');
      fit();
    },
    arrangeInside(locId) { layoutInside(scene, locId); reroute(); commit('layout-inside'); },
    resetRoutes() {
      for (const e of scene.edges.values()) { e.vias = []; e.mode = 'auto'; e.aPort = { mode: 'auto' }; e.bPort = { mode: 'auto' }; }
      reroute(); commit('reset-routes');
    },
    straighten(ids) {
      for (const id of ids || ui.selection) {
        const e = scene.edges.get(id);
        if (e) { e.vias = []; e.mode = 'auto'; }
      }
      reroute(); commit('straighten');
    },
    setNodeImage(devId, path) {
      const n = scene.nodes.get(devId);
      if (!n) return;
      n.imagePath = path ? `${path}${path.includes('?') ? '&' : '?'}v=${Date.now()}` : null;
      needsDraw = true;
    },
    commit,
    snapshotNow: () => ({ items: serialize(scene), meta: serializeMeta(scene, vp, ui) }),
    dispose() {
      flushSave();          // иначе последняя правка уедет в никуда
      disposed = true;
      clearTimeout(pendingSave);
      canvas.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('blur', onPointerUp);
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('visibilitychange', onVisibility);
      canvas.removeEventListener('dblclick', onDoubleClick);
      canvas.removeEventListener('contextmenu', onContextMenu);
      canvas.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKey);
    },
  };

  canvas.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerUp);
  window.addEventListener('blur', onPointerUp);
  window.addEventListener('beforeunload', onBeforeUnload);
  document.addEventListener('visibilitychange', onVisibility);
  canvas.addEventListener('dblclick', onDoubleClick);
  canvas.addEventListener('contextmenu', onContextMenu);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('keydown', onKey);

  setSpriteListener(() => { needsDraw = true; });

  resize();
  reroute();
  history.push(snapshot());
  histIndex = 0;
  requestAnimationFrame(frame);

  return api;
}
